/**
 * Structured-markdown section-response parser, ported verbatim from Anvil's
 * src/lib/section-response.ts. We ask the model for `## LABEL`-separated
 * markdown instead of JSON when the payload is a section's worth of prose —
 * JSON falls over on code fences, table pipes, Unicode arrows, and unescaped
 * quotes; markdown headers round-trip losslessly (TDD §7.6).
 */
export interface SectionPayload {
  body: string;
  impacts: string;
  risks: string;
  benefits: string;
}

/** Generic structured-markdown parser. Pass the upper-case labels you expect
 *  (e.g. ["BODY", "IMPACTS"]); returns a dict keyed by the lowercased labels.
 *  Each value is the markdown between that header and the next (or EOF). */
export function parseSections<K extends string>(
  text: string,
  labels: readonly K[],
): Record<Lowercase<K>, string> {
  const pattern = labels.join('|');
  const re = new RegExp(
    `(?:^|\\n)\\s*(?:#{1,6}\\s*|\\*\\*\\s*)?(${pattern})(?:\\s*\\*\\*)?\\s*:?\\s*\\n([\\s\\S]*?)(?=\\n\\s*(?:#{1,6}\\s*|\\*\\*\\s*)?(?:${pattern})(?:\\s*\\*\\*)?\\s*:?\\s*\\n|$)`,
    'gi',
  );
  const out = {} as Record<Lowercase<K>, string>;
  for (const l of labels) {
    out[l.toLowerCase() as Lowercase<K>] = '';
  }
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const key = m[1].toLowerCase() as Lowercase<K>;
    if (key in out && !out[key]) {
      out[key] = m[2].trim();
    }
  }
  return out;
}

export function parseSectionResponse(text: string): SectionPayload {
  const parsed = parseSections(text, ['BODY', 'IMPACTS', 'RISKS', 'BENEFITS'] as const);
  return {
    body: parsed.body,
    impacts: parsed.impacts,
    risks: parsed.risks,
    benefits: parsed.benefits,
  };
}

/** Prompt fragment instructing the model to emit a single-section payload in
 *  our structured-markdown format. Append after the task description. */
export const SECTION_OUTPUT_INSTRUCTIONS = `Output format — STRICT, plain markdown, NOT JSON, no fences wrapping the whole thing. Use exactly these four sections in this order, each introduced by a level-2 heading:

## BODY
<the section content as markdown — paragraphs, lists, tables, code blocks all fine. Do NOT include a heading for the section title itself.>

## IMPACTS
<markdown bullet list>

## RISKS
<markdown bullet list>

## BENEFITS
<markdown bullet list>

Do not output anything before BODY or after BENEFITS. The four headers are the only required separators.`;

/** Prompt fragment for drafting an ITEM-MODE section: emit the section body as
 *  a list of items in the modular-item convention (parsed by parseItems), with
 *  NO `## BODY` wrapper. Append after the task + ITEM_MODEL_GUIDE. */
export const ITEM_SECTION_OUTPUT_INSTRUCTIONS = `Output format — STRICT, plain markdown, NOT JSON, no fences wrapping the whole thing, no section-title heading. Output ONLY a list of items in this exact shape:

## <Item name>
_<specific slug: the thing + its one distinguishing detail — a count, key parameter, or the rule that makes it different; NOT a restatement of the name>_

<details: enumerate the concrete things to build (specific entities, values, examples) scoped to a first playable version; ~3–6 tight lines, no generic framing or boilerplate>

## <Next item name>
_<slug>_

<details…>

Use \`###\` sub-items under a \`##\` item only when it is genuinely a group of distinct things. Produce a handful of well-scoped items (roughly 3–6) — each a concrete thing to build, not an abstraction. State anything truly shared once (briefly, in the first item), never repeated per item. Do not output any text before the first \`##\` or after the last item.

To ADD entries to an EXISTING group (e.g. add trivia questions to a category, levels to a world, items to an inventory), re-emit that group's \`## Group name\` heading followed by ONLY the new \`### Entry\` children — each entry IS a \`###\` heading with its own slug + details. The app merges children by name and keeps the group's existing entries, so never re-list the entries already there, and never emit entries as a bare list or prose under the group body.`;
